<script lang="ts">
  import { getModalStore } from '@skeletonlabs/skeleton';
  import { browser } from '$app/environment';
  import { showToast } from '$lib/helpers/toastrHelper';
  import Button from '$lib/components/common/button/Button.svelte';

  const modalStore = getModalStore();

  const responseData = $modalStore[0].meta.responseData;
  const success = $modalStore[0].meta.success;
  const title = $modalStore[0].meta.title || 'Reckon XML Response';

  function handleClose() {
    modalStore.close();
  }

  function getDisplayData() {
    if (success && responseData) {
      return responseData;
    }
    return responseData;
  }

  async function copyResponse() {
    // For Reckon, we want to copy the XML directly
    const data = success && responseData?.xml ? responseData.xml : JSON.stringify(getDisplayData(), null, 2);
    if (!browser) {
      showToast({ title: 'Copy', message: 'Copy is not available on the server.', type: 'info' });
      return;
    }

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(data);
        showToast({ title: 'Copied', message: 'XML copied to clipboard', type: 'success' });
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = data;
        textarea.style.position = 'fixed';
        textarea.style.top = '0';
        textarea.style.left = '-9999px';
        textarea.setAttribute('readonly', '');
        document.body.appendChild(textarea);
        textarea.select();
        textarea.setSelectionRange(0, data.length);
        
        showToast({ 
          title: 'Manual Copy Required', 
          message: 'Press Ctrl+C (Cmd+C on Mac) to copy', 
          type: 'info' 
        });
        
        setTimeout(() => {
          document.body.removeChild(textarea);
        }, 5000);
      }
    } catch (err) {
      console.error('Failed to copy response:', err);
      showToast({ title: 'Copy Failed', message: 'Unable to copy to clipboard', type: 'error' });
    }
  }
</script>

<div class="card w-modal max-w-4xl p-6 space-y-4" data-testid="modal" role="dialog" aria-modal="true">
  <!-- Header -->
  <header class="flex justify-between items-center">
    <h3 class="text-lg font-bold">{title}</h3>
  </header>

  <!-- Body -->
  <section class="space-y-4">
    {#if success}
      <div class="rounded-lg bg-green-50 border border-green-200 p-4">
        <div class="flex items-center mb-2">
          <div class="w-4 h-4 rounded-full bg-green-500 mr-2"></div>
          <span class="text-green-800 font-medium">XML Generated Successfully</span>
        </div>
        {#if responseData?.invoiceId}
          <div class="text-sm text-green-700 mt-2">
            Invoice ID: {responseData.invoiceId}
            {#if responseData?.invoiceNumber}
              | Invoice Number: {responseData.invoiceNumber}
            {/if}
          </div>
        {/if}
      </div>
    {:else}
      <div class="rounded-lg bg-red-50 border border-red-200 p-4">
        <div class="flex items-center mb-2">
          <div class="w-4 h-4 rounded-full bg-red-500 mr-2"></div>
          <span class="text-red-800 font-medium">XML Generation Failed</span>
        </div>
      </div>
    {/if}

    <!-- Response Data -->
    <div class="space-y-2">
      <div class="block text-sm font-medium text-gray-700">
        {success ? 'Generated XML:' : 'Error Details:'}
      </div>
      <div class="bg-gray-50 border rounded-lg p-4 max-h-96 overflow-auto">
        {#if success && responseData?.xml}
          <pre class="text-xs whitespace-pre-wrap text-gray-800 font-mono">{responseData.xml}</pre>
        {:else}
          <pre class="text-xs whitespace-pre-wrap text-gray-800">{JSON.stringify(getDisplayData(), null, 2)}</pre>
        {/if}
      </div>
    </div>
  </section>

  <!-- Footer -->
  <footer class="flex justify-end">
    <div class="mr-2">
      <Button
        label="Copy XML"
        colour="secondary"
        size="medium"
        onClick={copyResponse}
      />
    </div>
    <Button
      label="Close"
      colour="primary"
      size="medium"
      onClick={handleClose}
    />
  </footer>
</div>
